import { useEffect, useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import axios from 'axios';
import config from '../../config.js';

import '../../styles/global.css';
import '../../styles/status.css';
import '../../styles/pagination.css';

import PageHeader from '../../components/general/pageHeader.js';
import PageTable from '../../components/general/pageTable';
import CreateGatePassForm from '../../components/forms/createGatePassForm';
import useAuthenticate from '../../components/authentication/authenticate';
import Loading from '../../components/general/loading.js';
import GatePassCards from '../../components/cards/gatePassCard.js';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

const RGatePass = () => {
    const { user, isLoading } = useAuthenticate();
    const queryClient = useQueryClient();

    const role = Object.keys(user?.user?.active || {})[0];
    const account = {
        accountId: user?.user?.accounts?.[role],
        accountName: user?.user?.email?.split('@')[0]?.split('.')[0]?.replace(/^./, c => c.toUpperCase())
    };

    const [selectedProperty, setSelectedProperty] = useState(null);
    const [addOverlayOpen, setAddOverlayOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 10;

    const { data: properties } = useQuery({
        queryKey: ['properties'],
        queryFn: fetchProperties,
    });

    const { data: units = [] } = useQuery({
        queryKey: ['units'],
        queryFn: fetchUnits,
    });

    const { data: gatePasses, isLoading: isTableLoading } = useQuery({
        queryKey: ['gatePassesByProperty', selectedProperty?.value, currentPage],
        queryFn: () => fetchGatePassesByProperty(selectedProperty?.value, currentPage, rowsPerPage),
        enabled: !!selectedProperty?.value,
        keepPreviousData: true,
    });

    useEffect(() => {
        if (properties?.length > 0 && !selectedProperty) {
            setSelectedProperty(properties[0]);
        }
    }, [properties, selectedProperty]);

    const unitOptions = useMemo(() => {
        return selectedProperty
            ? units
                .filter((u) => u.property?._id === selectedProperty?.value)
                .map((u) => ({
                    value: u._id,
                    unitNumber: u.unitNumber,
                }))
            : [];
    }, [units, selectedProperty]);

    const handleGatePassCreated = () => {
        queryClient.invalidateQueries(['gatePassesByProperty', selectedProperty?.value]);
        setAddOverlayOpen(false);
    };

    const totalPages = gatePasses?.totalPages || 1;
    const maxVisibleButtons = 10;
    const startPage = Math.floor((currentPage - 1) / maxVisibleButtons) * maxVisibleButtons + 1;
    const endPage = Math.min(startPage + maxVisibleButtons - 1, totalPages);

    return (
        <div className="full-page w-full">
            <PageHeader pageName="Gate Pass" />

            {isLoading ? (
                <Loading />
            ) : (
                <>
                    <div className="page-header flex flex-col md:flex-row gap-3">
                        <div className="w-full md:w-[300px]">
                            <Autocomplete
                                options={properties}
                                value={selectedProperty}
                                onChange={(e, newVal) => {
                                    if (newVal && newVal.value && newVal.label) {
                                        setSelectedProperty(newVal);
                                    } else {
                                        setSelectedProperty(null);
                                }
                                }}
                                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                                getOptionLabel={(option) =>
                                    typeof option === 'string'
                                        ? option
                                        : option?.label || option?.name || ''
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Property"
                                        variant="outlined"
                                        fullWidth
                                    />
                                )}
                            />
                        </div>
                        <div className="flex md:flex-row gap-1 w-full md:w-auto mt-2 md:mt-0">
                            <div className="w-full md:w-auto">
                                <button
                                    className="header-button w-full md:w-auto h-[50px] md:h-full text-[14px] md:text-[12px]"
                                    onClick={() => setAddOverlayOpen(true)}
                                >
                                    <i className="fas fa-receipt"></i> File Gate Pass
                                </button>
                            </div>
                        </div>

                    </div>

                    {/* Table or Cards */}
                    {isTableLoading ? (
                        <Loading />
                    ) : (
                        <div className="table-wrapper">
                            {/* Mobile Cards */}
                            <div className="block md:hidden mt-4">
                                <GatePassCards rows={gatePasses?.results || []} />
                            </div>

                            {/* Desktop Table */}
                            <div className="hidden md:block">
                                <PageTable
                                    rows={gatePasses?.results || []}
                                    columns={[
                                        { label: 'Unit', accessor: (row) => row.unit?.unitNumber, width: '10%' },
                                        {
                                            label: 'Status',
                                            accessor: (row) => (
                                                <span className={`request-status-badge ${row.status?.toLowerCase()}`}>
                                                {row.status}
                                                </span>
                                            ),
                                            width: '15%',
                                        },
                                        { label: 'Title', accessor: (row) => row.details?.title || 'N/A', width: '30%' },
                                        { label: 'Start Date', accessor: (row) => new Date(row.details?.startDate).toLocaleDateString(), width: '15%' },
                                        { label: 'End Date', accessor: (row) => new Date(row.details?.endDate).toLocaleDateString(), width: '15%' },
                                        { label: 'Requested On', accessor: (row) => new Date(row.createdAt).toLocaleDateString(), width: '15%' },
                                    ]}
                                    hasButtons={false}
                                />
                            </div>

                            {totalPages > 1 && (
                                <div className="pagination-controls">
                                    <div className="pagination-buttons">
                                        <button
                                            className="pagination-button"
                                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                            disabled={currentPage === 1}
                                        >
                                            <i className="fas fa-chevron-left"></i>
                                        </button>

                                        {startPage > 1 && (
                                            <button className="pagination-page" onClick={() => setCurrentPage(startPage - 1)}>
                                                ...
                                            </button>
                                        )}

                                        {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                                            const pageNum = startPage + i;
                                            return (
                                                <button
                                                    key={pageNum}
                                                    className={`pagination-page ${ currentPage === pageNum ? 'active' : ''}`}
                                                    onClick={() => setCurrentPage(pageNum)}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}

                                        {endPage < totalPages && (
                                            <button className="pagination-page" onClick={() => setCurrentPage(endPage + 1)}>
                                                ...
                                            </button>
                                        )}

                                        <button
                                            className="pagination-button"
                                            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                            disabled={currentPage === totalPages}
                                        >
                                            <i className="fas fa-chevron-right"></i>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Create Form Overlay */}
                    {addOverlayOpen && selectedProperty && (
                        <CreateGatePassForm
                            onClose={() => setAddOverlayOpen(false)}
                            property={{ _id: selectedProperty.value }}
                            unitOptions={unitOptions}
                            account={account}
                            role={role}
                            onRequestCreated={handleGatePassCreated}
                        />
                    )}
                </>
            )}
        </div>
    );
};

const fetchGatePassesByProperty = async (propertyId, page = 1, limit = 10) => {
    if (!propertyId) return { results: [], totalPages: 0, totalResults: 0 };

    try {
        const response = await axios.get(`${config.API_URL}/v2/request/property/${propertyId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
            params: { page, limit, sortBy: 'createdAt', sortOrder: 'desc', type: 'GatePass' },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching GatePass requests for property:', propertyId, error);
        return { results: [], totalPages: 0, totalResults: 0 };
    }
};

const fetchProperties = async () => {
    const response = await axios.get(`${config.API_URL}/v2/property`, {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}});
    return response.data.map(item => ({
        value: item._id,
        label: item.name,
    }));
};

const fetchUnits = async () => {
    const response = await axios.get(`${config.API_URL}/v2/unit`, {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}});
    return response.data;
};

export default RGatePass;